import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Grid, IconButton, Chip, Divider, Avatar } from "@mui/material";
import { Edit, Delete, Visibility, Add, People, MenuBook, CalendarToday, AttachMoney, Person, Assignment, ContentCopy } from "@mui/icons-material";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";
import Typography from "themeComponents/Typography";
import Button from "themeComponents/Button";
import DashboardLayout from "themeComponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "themeComponents/Navbars/DashboardNavbar";
import Footer from "themeComponents/Footer";
import Loader from "components/loader";
import { DeleteAlert } from "components/swalAlert";
import PageHeader from "./components/PageHeader";
import FilterBar, { FilterItem } from "./components/FilterBar";
import EmptyStateCard from "./components/EmptyStateCard";
import CourseStatusChip from "./components/CourseStatusChip";
import CourseCardShell from "./components/CourseCardShell";
import CourseMetaItem from "./components/CourseMetaItem";
import { formatCoursePrice, formatDateShort, getModuleCount } from "./utils/courseFormatters";

// Hooks
import { useCourse } from "hooks/useCourse";
import { useBranchFilter } from "hooks/useBranchFilter";
import type { Course } from "slices/courseSlice";
import useAuth from "hooks/useAuth";
import useCoursePermissions from "hooks/useCoursePermissions";
import { COMPCHECK_URL, courseApi, userApi } from "configs/api";
import { AppContext } from "context/appContext";
import { Formik, Form, useFormikContext } from "formik";
import FormikInput from "components/formikInput";
import {
  combineSeatMetrics,
  formatSeatsLabel,
  hasSeatAccess,
} from "utils/seatQuotaDisplay";

interface Creator {
  _id: string;
  name: string;
  email: string;
}

interface CourseFilterValues {
  creatorFilter: string;
  visibilityFilter: string;
  companyFilter: string;
  branchFilter: string;
}

function CourseFiltersObserver({
  onCreatorChange,
  onVisibilityChange,
  onCompanyChange,
  onBranchChange,
}: {
  onCreatorChange: (val: string) => void;
  onVisibilityChange: (val: string) => void;
  onCompanyChange?: (val: string) => void;
  onBranchChange?: (val: string) => void;
}) {
  const { values } = useFormikContext<CourseFilterValues>();
  const prev = useRef<CourseFilterValues>({ creatorFilter: "", visibilityFilter: "", companyFilter: "", branchFilter: "" });

  useEffect(() => {
    const p = prev.current;
    if (p.creatorFilter !== values.creatorFilter) onCreatorChange(values.creatorFilter);
    if (p.visibilityFilter !== values.visibilityFilter) onVisibilityChange(values.visibilityFilter);
    if (p.companyFilter !== values.companyFilter) onCompanyChange?.(values.companyFilter);
    if (p.branchFilter !== values.branchFilter) onBranchChange?.(values.branchFilter);
    prev.current = { ...values };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.creatorFilter, values.visibilityFilter, values.companyFilter, values.branchFilter]);

  return null;
}

function Courses(): React.JSX.Element {
  const navigate = useNavigate();
  const { courses, loading, getAllCourses, removeCourse, pagination } = useCourse();
  const {
    isAdmin,
    isCompany,
    isCompanyAdmin,
    isFellowAdmin,
    isCreator,
    isManagerOrHR,
    isManager,
    isHR,
    isEmployee,
    isSupervisor,
    isRestrictedEmployee,
    user,
  } = useAuth();
  const { showAlert } = useContext(AppContext);
  const [page, setPage] = useState(1);
  const [selectedCreator, setSelectedCreator] = useState<string>('');
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loadingCreators, setLoadingCreators] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [companies, setCompanies] = useState<Creator[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [visibilityFilter, setVisibilityFilter] = useState<string>('');
  const [cloningId, setCloningId] = useState<string | null>(null);
  const isDevMode = process.env.REACT_APP_ENV === 'dev';
  const filtersFormRef = useRef<any>(null);

  const isSuperAdmin = isAdmin;
    const {
      branches,
      loadingBranches,
      selectedBranchId,
      setSelectedBranchId,
      shouldShowBranchFilter,
    } = useBranchFilter({
      isAdmin,
      isCompanyAdmin,
      isFellowAdmin,
      isCompany,
      isManagerOrHR,
      user,
      selectedCompanyId,
    });

  const {
    canCreateCourse,
    canEditCourse,
    canShowCourseActions,
  } = useCoursePermissions({
    user,
    isAdmin,
    isCompany,
    isCompanyAdmin,
    isFellowAdmin,
    isCreator,
    isManager,
    isHR,
    isEmployee,
    isSupervisor,
    isRestrictedEmployee,
  });
  const canShowCreateCourseAction = canCreateCourse;
  const canAssignFromList = isSuperAdmin || isCompanyAdmin || isFellowAdmin;
  const managerHasBranch = isManagerOrHR && Boolean(user?.branchID);

  // Fetch creators for super admin filter
  useEffect(() => {
    if (isAdmin) {
      setLoadingCreators(true);
      userApi.get('/api/users', { params: { role: 'creator', limit: 100 } })
        .then((response) => {
          if (response.data.status) {
            setCreators(response.data.data?.data || []);
          }
        })
        .catch((error) => {
          console.error('Failed to fetch creators:', error);
        })
        .finally(() => {
          setLoadingCreators(false);
        });
    }
  }, [isAdmin]);

  // Fetch assigned companies for company admin / fellow admin filter, or all companies for super admin
  useEffect(() => {
    if (isSuperAdmin || isCompanyAdmin || isFellowAdmin) {
      setLoadingCompanies(true);
      
      const fetchCompanies = async () => {
        try {
          let assignedCompanies: string[] = [];

          // SuperAdmin: fetch all companies
          if (isSuperAdmin) {
            const companyRes = await userApi.get('/api/users', { params: { role: 'company', limit: 500 } });
            if (companyRes.data.status) {
              setCompanies(companyRes.data.data?.data || []);
            }
          } else {
            // CompanyAdmin: get from user.assignedCompanies directly
            if (isCompanyAdmin) {
              assignedCompanies = user?.assignedCompanies || [];
            }
            // FellowAdmin: fetch parent admin to get their assignedCompanies
            else if (isFellowAdmin && user?.adminID) {
              try {
                const parentRes = await userApi.get(`/api/users/${user.adminID}`);
                assignedCompanies = parentRes.data?.data?.assignedCompanies || [];
              } catch {
                assignedCompanies = [];
              }
            }

            // Now fetch the company details for those IDs
            if (assignedCompanies.length > 0) {
              const companyRes = await userApi.get('/api/users', { params: { ids: assignedCompanies.join(','), limit: 100 } });
              if (companyRes.data.status) {
                setCompanies(companyRes.data.data?.data || []);
              }
            } else {
              setCompanies([]);
            }
          }
        } catch (error) {
          console.error('Failed to fetch companies:', error);
          setCompanies([]);
        } finally {
          setLoadingCompanies(false);
        }
      };

      fetchCompanies();
    }
  }, [isSuperAdmin, isCompanyAdmin, isFellowAdmin, user?.assignedCompanies, user?.adminID]);

  useEffect(() => {
    getAllCourses(page, 12, selectedCreator || undefined, undefined, visibilityFilter || undefined, selectedCompanyId || undefined, selectedBranchId || undefined);
  }, [page, selectedCreator, visibilityFilter, selectedCompanyId, selectedBranchId, getAllCourses]);

  const handleClone = async (courseId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCloningId(courseId);
    try {
      const response = await courseApi.post(`/api/courses/${courseId}/clone`);
      const cloned = response.data.data;
      showAlert('Course cloned successfully', 'success', 'Success');
      getAllCourses(page, 12, selectedCreator || undefined, undefined, visibilityFilter || undefined);
      navigate(`/courses/${cloned.id}/edit`);
    } catch (error: any) {
      showAlert(error.response?.data?.message || 'Failed to clone course', 'error', 'Error');
    } finally {
      setCloningId(null);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    const confirmed = await DeleteAlert(`Are you sure you want to delete "${title}"?`);
    if (confirmed) {
      await removeCourse(id);
      getAllCourses(page, 12);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={0} {...({} as any)}>
        <PageHeader
          title="Courses"
          actions={
            canShowCreateCourseAction ? (
              <Button
                variant="gradient"
                color="info"
                size="medium"
                onClick={() => navigate("/courses/create")}
              >
                <Add sx={{ mr: 1 }} /> Create Course
              </Button>
            ) : undefined
          }
        />

        <Box mb={3} width="100%" minWidth={0}>
          <Formik
            innerRef={filtersFormRef}
            initialValues={{ creatorFilter: "", visibilityFilter: "", companyFilter: "", branchFilter: "" }}
            onSubmit={() => {}}
          >
            <Form>
              <FilterBar>
                <CourseFiltersObserver
                  onCreatorChange={(val) => { setSelectedCreator(val); setPage(1); }}
                  onVisibilityChange={(val) => { setVisibilityFilter(val); setPage(1); }}
                  onCompanyChange={(val) => { setSelectedCompanyId(val); setPage(1); }}
                  onBranchChange={(val) => { setSelectedBranchId(val); setPage(1); }}
                />
                {isSuperAdmin && (
                  <FilterItem>
                    <FormikInput
                      name="creatorFilter"
                      type="dropdown"
                      placeholder="Filter by Creator"
                      options={[{ _id: "", name: "All Creators" }, ...creators.map((c) => ({ _id: c._id, name: c.name || c.email }))]}
                      optionsLabel="name"
                      size="small"
                      disabled={loadingCreators}
                    />
                  </FilterItem>
                )}
                {(isSuperAdmin || isCompanyAdmin || isFellowAdmin) && (
                  <FilterItem>
                    <FormikInput
                      name="companyFilter"
                      type="dropdown"
                      placeholder="Filter by Company"
                      options={[{ _id: "", name: "All Companies" }, ...companies.map((c) => ({ _id: c._id, name: c.name || c.email }))]}
                      optionsLabel="name"
                      size="small"
                      disabled={loadingCompanies}
                    />
                  </FilterItem>
                )}
                {shouldShowBranchFilter && (
                  <FilterItem>
                    <FormikInput
                      name="branchFilter"
                      type="dropdown"
                      placeholder="Filter by Branch"
                      options={[{ _id: "", name: "All Branches" }, ...branches.map((b) => ({ _id: b._id, name: b.name || b.email || b._id }))]}
                      optionsLabel="name"
                      size="small"
                      disabled={loadingBranches}
                    />
                  </FilterItem>
                )}
                {isCompany && (
                  <FilterItem>
                    <FormikInput
                      name="visibilityFilter"
                      type="dropdown"
                      placeholder="Course Source"
                      options={[
                        { _id: "", name: "All" },
                        { _id: "created", name: "Created by Me" },
                        { _id: "assigned", name: "Assigned to Company" },
                      ]}
                      optionsLabel="name"
                      size="small"
                    />
                  </FilterItem>
                )}
                {isManagerOrHR && managerHasBranch && (
                  <FilterItem>
                    <FormikInput
                      name="visibilityFilter"
                      type="dropdown"
                      placeholder="Course Source"
                      options={[
                        { _id: "", name: "All" },
                        { _id: "me", name: "Assigned to Me" },
                        { _id: "branch", name: "Assigned to Branch" },
                      ]}
                      optionsLabel="name"
                      size="small"
                    />
                  </FilterItem>
                )}
                {isManagerOrHR && !managerHasBranch && (
                  <FilterItem>
                    <FormikInput
                      name="visibilityFilter"
                      type="dropdown"
                      placeholder="Course Source"
                      options={[
                        { _id: "", name: "All" },
                        { _id: "created", name: "Created by Company" },
                        { _id: "assigned", name: "Assigned to Company" },
                      ]}
                      optionsLabel="name"
                      size="small"
                    />
                  </FilterItem>
                )}
              </FilterBar>
            </Form>
          </Formik>
        </Box>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8} {...({} as any)}>
            <Loader loading={true} {...({} as any)} />
          </Box>
        ) : courses.length === 0 ? (
          <EmptyStateCard
            title="No courses found"
            actionLabel={canShowCreateCourseAction ? "Create Your First Course" : undefined}
            onAction={canShowCreateCourseAction ? () => navigate("/courses/create") : undefined}
          />
        ) : (
          <>
            <Grid container spacing={3}>
              {courses.map((course: Course) => {
                const listSeatSummary =
                  isCompany || isManagerOrHR
                    ? combineSeatMetrics({
                        purchase: course.purchaseInfo?.hasAccess
                          ? {
                              quotaPurchased: Number(course.purchaseInfo?.quotaPurchased ?? 0),
                              quotaUsed: Number(course.purchaseInfo?.quotaUsed ?? 0),
                              quotaAvailable: Number(course.purchaseInfo?.quotaAvailable ?? 0),
                            }
                          : undefined,
                        hasPurchaseAccess: Boolean(course.purchaseInfo?.hasAccess),
                        free: course.freeAssignmentInfo ?? undefined,
                        assignmentIsFreeAccess: course.assignmentIsFreeAccess === true,
                      })
                    : null;
                return (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={course.id}>
                  <CourseCardShell onClick={() => navigate(`/courses/${course.id}`)}>
                      {/* Thumbnail Image */}
                      {course.thumbnailURL && (
                        <Box
                          sx={{
                            width: '100%',
                            height: 160,
                            mb: 2,
                            borderRadius: 1,
                            overflow: 'hidden',
                            backgroundColor: 'grey.100',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          {...({} as any)}
                        >
                          <img
                            src={`${COMPCHECK_URL}/api/${course.thumbnailURL}`}
                            alt={course.title}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'fill',
                            }}
                            onError={(e) => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </Box>
                      )}

                      {/* Header with status and actions */}
                      <Box display="flex" justifyContent="space-between" alignItems="start" mb={1.5} {...({} as any)}>
                        <Box display="flex" gap={0.5} flexWrap="wrap" {...({} as any)}>
                          <CourseStatusChip status={course.status} size="small" />
                          {course.visibilitySource && (
                            <Chip
                              label={
                                course.visibilitySource === 'created_by_company' ? 'Created' :
                                course.visibilitySource === 'assigned_to_company' ? 'Assigned' :
                                course.visibilitySource === 'assigned_to_branch' ? 'To Branch' :
                                course.visibilitySource === 'assigned_to_me' ? 'To Me' :
                                course.visibilitySource === 'purchased' ? 'Purchased' :
                                course.visibilitySource
                              }
                              color={
                                course.visibilitySource === 'created_by_company' ? 'primary' :
                                course.visibilitySource === 'assigned_to_company' ? 'info' :
                                course.visibilitySource === 'assigned_to_branch' ? 'secondary' :
                                course.visibilitySource === 'purchased' ? 'success' :
                                'default'
                              }
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                        {canShowCourseActions(course) && (
                          <Box onClick={(e) => e.stopPropagation()} {...({} as any)}>
                            {canEditCourse(course) && (
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/courses/${course.id}/edit`)}
                                sx={{ mr: 0.5 }}
                                title="Edit Course"
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                            {isDevMode && (
                              <IconButton
                                size="small"
                                onClick={(e) => handleClone(course.id, e)}
                                disabled={cloningId === course.id}
                                sx={{ mr: 0.5 }}
                                title="Clone Course"
                              >
                                <ContentCopy fontSize="small" color="warning" />
                              </IconButton>
                            )}
                            {canEditCourse(course) && (
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(course.id, course.title)}
                                title="Delete Course"
                              >
                                <Delete fontSize="small" color="error" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </Box>

                      {/* Course Title */}
                      <Typography variant="h6" fontWeight="bold" mb={1} sx={{ minHeight: '3em' }} {...({} as any)}>
                        {course.title}
                      </Typography>

                      {/* Course Description */}
                      <Typography
                        variant="body2"
                        color="text"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          mb: 2,
                          minHeight: '3em',
                        }}
                        {...({} as any)}
                      >
                        {course.description || "No description available"}
                      </Typography>

                      <Divider sx={{ my: 1.5 }} />

                      {/* Course Stats */}
                      <Box display="flex" flexDirection="column" gap={1} mb={2} {...({} as any)}>
                        <CourseMetaItem
                          icon={<MenuBook fontSize="small" color="action" />}
                          text={`${getModuleCount(course.moduleCount, course.modules)} Module${getModuleCount(course.moduleCount, course.modules) !== 1 ? "s" : ""}`}
                        />
                        <CourseMetaItem
                          icon={<People fontSize="small" color="action" />}
                          text={`${Math.max(0, Number(course.enrollmentCount ?? (course as any).enrollment_count ?? 0) || 0)} Enrolled`}
                        />
                        <CourseMetaItem
                          icon={<AttachMoney fontSize="small" color="action" />}
                          text={formatCoursePrice(course.price, course.isFree)}
                        />
                        <CourseMetaItem
                          icon={<CalendarToday fontSize="small" color="action" />}
                          text={formatDateShort(course.createdAt)}
                        />
                        <CourseMetaItem
                          icon={<Person fontSize="small" color="action" />}
                          text={course.instructorName && course.instructorName.trim() !== "" ? course.instructorName : "Unknown"}
                          truncate
                        />
                        {(isCompany || isManagerOrHR) && course.quotaInfo && (
                          <Box display="flex" alignItems="center" gap={1} {...({} as any)}>
                            <Chip
                              label={`Quota: ${formatSeatsLabel(course.quotaInfo.quotaAvailable)}/${formatSeatsLabel(course.quotaInfo.quota)}`}
                              size="small"
                              color={hasSeatAccess(course.quotaInfo.quotaAvailable) ? "success" : "error"}
                              variant="outlined"
                            />
                          </Box>
                        )}
                        {listSeatSummary && (isCompany || isManagerOrHR) && (
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" {...({} as any)}>
                            <Chip
                              label={`Total Available: ${formatSeatsLabel(listSeatSummary.total)}`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                            <Chip
                              label={`Seats Used: ${listSeatSummary.used}`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                            <Chip
                              label={`Total Remaining: ${formatSeatsLabel(listSeatSummary.remaining)}`}
                              size="small"
                              color={hasSeatAccess(listSeatSummary.remaining) ? "success" : "error"}
                              variant="outlined"
                            />
                          </Box>
                        )}
                      </Box>

                      {/* Action Buttons */}
                      <Box mt="auto" display="flex" gap={1} {...({} as any)}>
                        <Button
                          variant="outlined"
                          color="info"
                          size="small"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/courses/${course.id}`);
                          }}
                          {...({} as any)}
                        >
                          <Visibility sx={{ mr: 0.5, fontSize: "16px" }} />
                          View
                        </Button>
                        {canAssignFromList && String(course.status || "").toLowerCase() !== "draft" && (
                          <IconButton
                            size="small"
                            color="success"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/courses/${course.id}?assign=true`);
                            }}
                            title="Assign Course"
                            {...({} as any)}
                          >
                            <Assignment fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                  </CourseCardShell>
                </Grid>
                );
              })}
            </Grid>
          </>
        )}

        {pagination.totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4} {...({} as any)}>
            <Button
              variant="outlined"
              color="info"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              {...({ sx: { mr: 2 } } as any)}
            >
              Previous
            </Button>
            <Typography variant="body2" {...({ sx: { alignSelf: "center", mx: 2 } } as any)}>
              Page {page} of {pagination.totalPages}
            </Typography>
            <Button
              variant="outlined"
              color="info"
              disabled={page === pagination.totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}

export default Courses;
